import { useEffect, useState } from "react";
import { Camera, Phone, Mic, CheckCircle2, XCircle, AlertTriangle, Loader2, Gift, ExternalLink, Check } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { EVENT_TYPES, fmtEUR, fmtDate } from "@/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const KINDS = [
  ["photobooth", "Photobooth", Camera],
  ["agb", "Audio Guest Book", Phone],
  ["cabine", "Cabine Audio Guest Book", Mic],
];

const KIND_META = Object.fromEntries(KINDS.map(([k, label, Icon]) => [k, { label, Icon }]));

const BOOKING_LINKS = {
  "100": "https://www.photobooth54.fr/product/12619845/acompte-photobooth-vintage-100-photos-a-250",
  "200": "https://www.photobooth54.fr/product/12619893/acompte-photobooth-vintage-200-photos-a-280",
  "400": "https://www.photobooth54.fr/product/12619900/acompte-photobooth-vintage-400-photos-a-320",
  "700": "https://www.photobooth54.fr/product/20859115/acompte-photobooth-vintage-700-photos-a-390",
  agb: "https://www.photobooth54.fr/product/13625049/location-audio-guest-book-seul",
  cabine: "https://www.photobooth54.fr/product/20662643/location-cabine-audio-guest-book-vintage",
};

const GLOBALS = {
  all: { label: "Ensemble disponible", cls: "border-emerald-300 bg-emerald-50", text: "text-emerald-700", Icon: CheckCircle2 },
  partial: { label: "Disponibilité partielle", cls: "border-amber-300 bg-amber-50", text: "text-amber-800", Icon: AlertTriangle },
  none: { label: "Indisponible à cette date", cls: "border-rose-300 bg-rose-50", text: "text-rose-700", Icon: XCircle },
  weekday: { label: "Disponibilité à confirmer", cls: "border-amber-300 bg-amber-50", text: "text-amber-800", Icon: AlertTriangle },
  provisional: { label: "Disponibilité à confirmer", cls: "border-amber-300 bg-amber-50", text: "text-amber-800", Icon: AlertTriangle },
  last_minute: { label: "Demande de dernière minute — à confirmer", cls: "border-amber-300 bg-amber-50", text: "text-amber-800", Icon: AlertTriangle },
  zone_confirm: { label: "Disponibilité à confirmer — solution possible", cls: "border-amber-300 bg-amber-50", text: "text-amber-800", Icon: AlertTriangle },
};

function ContactButtons({ phone, email, prefix }) {
  const digits = String(phone || "").replace(/\D/g, "");
  const intl = digits ? (digits.startsWith("0") ? `+33${digits.slice(1)}` : `+${digits}`) : "";
  const display = digits.replace(/(\d{2})(?=\d)/g, "$1 ").trim();
  const subject = encodeURIComponent("Demande de disponibilité photobooth — lieu en limite de zone");
  return (
    <div className="mt-2 space-y-2" data-testid={`${prefix}-buttons`}>
      {digits && (
        <a href={`tel:${intl}`} data-testid={`${prefix}-call`}
          className="flex items-center justify-center gap-2 w-full rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-sm h-11 transition-colors">
          NOUS APPELER — {display} <Phone className="h-4 w-4" />
        </a>
      )}
      {digits && (
        <a href={`sms:${intl}`} data-testid={`${prefix}-sms`}
          className="flex items-center justify-center gap-2 w-full rounded-xl border-2 border-slate-900 text-slate-900 font-extrabold text-sm h-11 transition-colors hover:bg-slate-100">
          ENVOYER UN SMS
        </a>
      )}
      <a
        href={email ? `mailto:${email}?subject=${subject}` : "https://www.photobooth54.fr/contact"}
        {...(!email ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        data-testid={`${prefix}-mail`}
        className="flex items-center justify-center gap-2 w-full rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-extrabold text-sm h-11 transition-colors"
      >
        NOUS ÉCRIRE <ExternalLink className="h-4 w-4" />
      </a>
    </div>
  );
}

export default function PublicCheck() {
  const [settings, setSettings] = useState(null);
  const [kinds, setKinds] = useState(["photobooth"]);
  const [date, setDate] = useState("");
  const [type, setType] = useState("Mariage");
  const [typeOther, setTypeOther] = useState("");
  const [city, setCity] = useState("");
  const [postcode, setPostcode] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [formula, setFormula] = useState("200");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [client, setClient] = useState({ name: "", phone: "", email: "" });
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [contactSent, setContactSent] = useState(false);
  const [sendingContact, setSendingContact] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)); }, []);

  const isBooth = kinds.includes("photobooth");

  const resetResult = () => { setResult(null); setShowForm(false); setSent(false); setError(""); setContactSent(false); };

  const toggleKind = (k) => {
    resetResult();
    setKinds((ks) => {
      const next = ks.includes(k) ? ks.filter((x) => x !== k) : [...ks, k];
      return next.length ? next : ks;
    });
  };

  const onCityChange = (v) => {
    setCity(v);
    setShowSug(true);
    if (v.trim().length >= 2) {
      api.get("/cities", { params: { q: v } }).then((r) => setSuggestions(r.data)).catch(() => {});
    } else {
      setSuggestions([]);
    }
  };

  const check = async () => {
    setError("");
    setLoading(true);
    setResult(null);
    setShowForm(false);
    setSent(false);
    setContactSent(false);
    try {
      if (!date) { setError("Merci de renseigner la date"); setLoading(false); return; }
      if (isBooth && (!city.trim() || !formula)) { setError("Merci de renseigner la date, le lieu et la formule"); setLoading(false); return; }
      const r = await api.post("/public/multi-check", { kinds, date, city: city.trim(), postcode: postcode.trim(), formula });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Vérification impossible pour le moment. Réessayez dans un instant.");
    } finally {
      setLoading(false);
    }
  };

  const sendContact = async () => {
    if (!contact.name.trim() && !contact.email.trim() && !contact.phone.trim()) {
      toast.error("Renseignez au moins une coordonnée");
      return;
    }
    setSendingContact(true);
    try {
      await api.post(`/public/consultation/${result.consultation_id}/contact`, contact);
      setContactSent(true);
    } catch {
      toast.error("Envoi impossible pour le moment — vous pouvez réserver directement ci-dessous.");
    } finally {
      setSendingContact(false);
    }
  };

  const pbR = result?.results?.photobooth || null;
  const eqR = (k) => result?.results?.[k] || null;
  const multi = result ? result.kinds.length > 1 : false;
  const g = result ? GLOBALS[result.global] : null;
  const cabineOk = Boolean(kinds.includes("cabine") && eqR("cabine")?.available);
  const agbOk = Boolean(kinds.includes("agb") && eqR("agb")?.available);
  const agbOffert = agbOk && pbR?.available;
  const cabinePrice = eqR("cabine")?.price ?? 0;
  const total = pbR?.available ? pbR.formula.price + pbR.supplement + (cabineOk ? cabinePrice : 0) : 0;

  const sendRequest = async () => {
    if (!client.name.trim()) { setError("Merci d'indiquer votre nom"); return; }
    setSending(true);
    setError("");
    try {
      await api.post("/public/demande", {
        kinds, client, date, city: city.trim(), postcode: postcode.trim(),
        formula, event_type: type, event_type_other: typeOther, message,
      });
      setSent(true);
      setShowForm(false);
    } catch (e) {
      setError(e.response?.data?.detail || "Envoi impossible — réessayez ou contactez-nous directement.");
    } finally {
      setSending(false);
    }
  };

  const eqSubline = (k, r) => {
    if (!r?.available) return null;
    if (k === "agb" && agbOffert) return "Offert avec votre photobooth — remis lors de la livraison, rien à retirer.";
    const price = r.price > 0 ? fmtEUR(r.price) : "Tarif communiqué lors de la confirmation";
    return `${price} — retrait et retour à Lesménils (54700), généralement en soirée entre 19h00 et 20h30 — horaire à confirmer`;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="public-page">
      <header className="bg-slate-900 text-white px-4 py-5">
        <div className="max-w-xl mx-auto flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-sky-500 flex items-center justify-center shrink-0">
            <Camera className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="font-heading font-extrabold text-lg tracking-tight leading-none">Photobooth54</p>
            <p className="text-xs text-slate-400 mt-1">Location de photobooth — Lesménils, entre Nancy et Metz</p>
          </div>
        </div>
      </header>

      <main className="max-w-xl mx-auto p-4 sm:p-6 space-y-5">
        <section className="rounded-2xl border-2 border-sky-200 bg-white p-4 sm:p-6 space-y-4">
          <h1 className="font-heading text-xl sm:text-2xl font-extrabold tracking-tight">Vérifiez votre date</h1>

          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Quel matériel vous intéresse ? (un ou plusieurs)</p>
            <div className="grid grid-cols-3 gap-2" data-testid="public-kind-selector">
              {KINDS.map(([k, label, Icon]) => {
                const active = kinds.includes(k);
                return (
                  <button
                    key={k}
                    type="button"
                    data-testid={`public-kind-${k}`}
                    aria-pressed={active}
                    onClick={() => toggleKind(k)}
                    className={`relative flex flex-col items-center gap-1.5 rounded-xl border-2 px-2 py-3 text-center transition-colors ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-sky-400"}`}
                  >
                    <span className={`absolute top-1.5 right-1.5 flex h-4 w-4 items-center justify-center rounded border transition-colors ${active ? "border-white bg-white text-slate-900" : "border-slate-300 text-transparent"}`}>
                      <Check className="h-3 w-3" />
                    </span>
                    <Icon className="h-5 w-5" />
                    <span className="text-xs font-bold leading-tight">{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Date de l'événement</label>
              <Input data-testid="public-date-input" type="date" min={new Date().toISOString().slice(0, 10)} value={date} onChange={(e) => setDate(e.target.value)} />
              {date && <p data-testid="public-date-day" className="text-xs font-semibold text-sky-700">{fmtDate(date)}</p>}
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Type d'événement</label>
              <select data-testid="public-type-select" value={type} onChange={(e) => setType(e.target.value)} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
                {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {type === "Autre" && (
              <Input data-testid="public-type-other" value={typeOther} onChange={(e) => setTypeOther(e.target.value)} placeholder="Précisez le type d'événement" />
            )}
            {!isBooth && (
              <p data-testid="public-equipment-note" className="text-xs text-slate-500 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                Retrait et retour directement à Lesménils (54700) — aucune livraison, aucun supplément. Retrait généralement prévu en soirée entre 19h00 et 20h30 — horaire à confirmer ensemble.
              </p>
            )}
            {isBooth && (
              <>
                <div className="grid grid-cols-[1fr_110px] gap-2">
                  <div className="space-y-1 relative">
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Ville</label>
                    <Input
                      data-testid="public-city-input"
                      value={city}
                      onChange={(e) => onCityChange(e.target.value)}
                      onFocus={() => setShowSug(true)}
                      onBlur={() => setTimeout(() => setShowSug(false), 150)}
                      placeholder="Ex. Metz, Nancy…"
                      autoComplete="off"
                    />
                    {showSug && suggestions.length > 0 && (
                      <ul data-testid="public-city-suggestions" className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-48 overflow-auto">
                        {suggestions.map((s) => (
                          <li key={s.label}>
                            <button
                              type="button"
                              data-testid={`public-city-option-${s.name}`}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-sky-50 transition-colors"
                              onMouseDown={() => { setCity(s.name); setPostcode(s.postcode || ""); setSuggestions([]); setShowSug(false); }}
                            >
                              {s.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Code postal</label>
                    <Input data-testid="public-postcode-input" value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="54700" />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Formule photobooth souhaitée</label>
                  <select data-testid="public-formula-select" value={formula} onChange={(e) => setFormula(e.target.value)} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
                    {(settings?.formulas || []).filter((f) => f.id !== "autre").map((f) => (
                      <option key={f.id} value={f.id}>{f.label} — {fmtEUR(f.price)}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>

          {error && <p data-testid="public-error" className="text-sm font-medium text-rose-600">{error}</p>}

          <Button data-testid="public-check-submit" onClick={check} disabled={loading} className="w-full bg-sky-500 hover:bg-sky-600 text-white font-semibold h-11 transition-colors">
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Vérifier la disponibilité
          </Button>
        </section>

        {result && g && result.weekday && (
          <section data-testid="public-weekday-panel" className={`rounded-2xl border p-5 space-y-4 ${g.cls}`}>
            <p data-testid="public-weekday-verdict" className={`font-heading text-lg font-extrabold tracking-tight flex items-center gap-2 ${g.text}`}>
              🟠 DISPONIBILITÉ À CONFIRMER
            </p>
            <div className="space-y-2 text-sm text-slate-700">
              <p className="font-semibold">Votre événement a lieu en semaine ({fmtDate(date)}).</p>
              <p>Les prestations Photobooth54 sont principalement organisées le week-end. Les demandes du lundi au vendredi restent cependant possibles selon nos disponibilités et notre organisation.</p>
              <p>Contactez-nous afin que nous puissions vérifier ensemble la faisabilité de votre événement — nous étudions chaque demande avec plaisir.</p>
            </div>
            <a
              href="https://www.photobooth54.fr/contact"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="public-weekday-contact"
              className="flex items-center justify-center gap-2 w-full rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-extrabold text-base h-12 transition-colors"
            >
              NOUS CONTACTER <ExternalLink className="h-4 w-4" />
            </a>
            <p className="text-xs text-slate-500">Vous pouvez aussi laisser vos coordonnées ci-dessous : nous vous recontactons pour étudier votre demande.</p>
          </section>
        )}

        {result && result.provisional && (
          <section data-testid="public-provisional-panel" className="rounded-2xl border p-5 space-y-4 border-amber-300 bg-amber-50">
            <p data-testid="public-provisional-verdict" className="font-heading text-lg font-extrabold tracking-tight flex items-center gap-2 text-amber-800">
              🟠 DISPONIBILITÉ À CONFIRMER
            </p>
            <div className="space-y-2 text-sm text-slate-700">
              <p className="font-semibold">Cette date est actuellement en cours de validation dans notre planning.</p>
              <p>Nous vous invitons à nous contacter afin que nous puissions vous confirmer rapidement si la prestation est possible.</p>
            </div>
            <a
              href="https://www.photobooth54.fr/contact"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="public-provisional-contact"
              className="flex items-center justify-center gap-2 w-full rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-extrabold text-base h-12 transition-colors"
            >
              NOUS CONTACTER <ExternalLink className="h-4 w-4" />
            </a>
            <p className="text-xs text-slate-500">Vous pouvez aussi laisser vos coordonnées ci-dessous : nous vous recontactons pour étudier votre demande.</p>
          </section>
        )}

        {result && result.last_minute && (
          <section data-testid="public-lastminute-panel" className="rounded-2xl border p-5 space-y-4 border-amber-300 bg-amber-50">
            <p data-testid="public-lastminute-verdict" className="font-heading text-lg font-extrabold tracking-tight flex items-center gap-2 text-amber-800">
              🟠 DEMANDE DE DERNIÈRE MINUTE — À CONFIRMER
            </p>
            <div className="space-y-2 text-sm text-slate-700">
              <p className="font-semibold">Votre événement approche !</p>
              <p>Pour une demande à cette date, merci de nous contacter directement afin que nous puissions vérifier rapidement si nous pouvons encore organiser la prestation.</p>
            </div>
            {(() => {
              const digits = String(settings?.contact_phone || "").replace(/\D/g, "");
              if (!digits) return null;
              const display = digits.replace(/(\d{2})(?=\d)/g, "$1 ").trim();
              const href = digits.startsWith("0") ? `tel:+33${digits.slice(1)}` : `tel:+${digits}`;
              return (
                <a
                  href={href}
                  data-testid="public-lastminute-call"
                  className="flex items-center justify-center gap-2 w-full rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-base h-12 transition-colors"
                >
                  NOUS APPELER — {display} <Phone className="h-4 w-4" />
                </a>
              );
            })()}
            <a
              href="https://www.photobooth54.fr/contact"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="public-lastminute-contact"
              className="flex items-center justify-center gap-2 w-full rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-extrabold text-base h-12 transition-colors"
            >
              NOUS CONTACTER <ExternalLink className="h-4 w-4" />
            </a>
            <p className="text-xs text-slate-500">Vous pouvez aussi laisser vos coordonnées ci-dessous : nous vous recontactons pour étudier votre demande.</p>
          </section>
        )}

        {result && result.global === "zone_confirm" && (
          <section data-testid="public-zone-panel" className="rounded-2xl border p-5 space-y-4 border-amber-300 bg-amber-50">
            <p data-testid="public-zone-verdict" className="font-heading text-lg font-extrabold tracking-tight flex items-center gap-2 text-amber-800">
              🟠 PHOTOBOOTH INDISPONIBLE POUR CE LIEU — SOLUTION POSSIBLE
            </p>
            <div className="space-y-2 text-sm text-slate-700">
              <p>Ce lieu est légèrement au-delà de notre zone habituelle de livraison.</p>
              <p className="font-semibold">Selon la date, une solution peut être trouvée : contactez-nous directement et nous verrons ensemble ce qui est possible.</p>
            </div>
            {(() => {
              const digits = String(settings?.contact_phone || "").replace(/\D/g, "");
              const intl = digits ? (digits.startsWith("0") ? `+33${digits.slice(1)}` : `+${digits}`) : "";
              const display = digits.replace(/(\d{2})(?=\d)/g, "$1 ").trim();
              return (
                <>
                  {digits && (
                    <a href={`tel:${intl}`} data-testid="public-zone-call" className="flex items-center justify-center gap-2 w-full rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-base h-12 transition-colors">
                      NOUS APPELER — {display} <Phone className="h-4 w-4" />
                    </a>
                  )}
                  {digits && (
                    <a href={`sms:${intl}`} data-testid="public-zone-sms" className="flex items-center justify-center gap-2 w-full rounded-xl border-2 border-slate-900 text-slate-900 font-extrabold text-base h-12 transition-colors hover:bg-slate-100">
                      ENVOYER UN SMS
                    </a>
                  )}
                </>
              );
            })()}
            <a href="https://www.photobooth54.fr/contact" target="_blank" rel="noopener noreferrer" data-testid="public-zone-contact"
              className="flex items-center justify-center gap-2 w-full rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-extrabold text-base h-12 transition-colors">
              NOUS ÉCRIRE <ExternalLink className="h-4 w-4" />
            </a>
            <p className="text-xs text-slate-500">Vous pouvez aussi laisser vos coordonnées ci-dessous : nous vous recontactons pour étudier une solution.</p>
          </section>
        )}

        {result && g && !result.weekday && !result.provisional && !result.last_minute && result.global !== "zone_confirm" && (
          <section data-testid="public-result" className={`rounded-2xl border p-5 space-y-4 ${g.cls}`}>
            {(result.provisional_kinds || []).length > 0 && (
              <div data-testid="public-provisional-notice" className="rounded-xl border border-amber-300 bg-amber-100/70 p-3.5 text-sm text-amber-900">
                🟠 <strong>Disponibilité à confirmer</strong> pour : {(result.provisional_kinds || []).map((k) => KIND_META[k].label).join(" + ")} — cette période est en cours de validation dans notre planning.{" "}
                <a href="https://www.photobooth54.fr/contact" target="_blank" rel="noopener noreferrer" data-testid="public-provisional-notice-contact" className="font-bold underline">Nous contacter</a>
              </div>
            )}
            <ul className="space-y-2.5">
              {result.kinds.map((k) => {
                const r = result.results[k];
                const ok = Boolean(r?.available);
                const provk = (result.provisional_kinds || []).includes(k);
                const { label, Icon } = KIND_META[k];
                return (
                  <li key={k} data-testid={`public-line-${k}`} className="flex items-start gap-2.5">
                    <Icon className="h-5 w-5 mt-0.5 text-slate-500 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm">
                        <span className="font-bold text-slate-800">{label}</span>
                        <span
                          data-testid={multi ? `public-line-${k}-status` : (k === "photobooth" ? "public-line-photobooth-status" : "public-equipment-verdict")}
                          className={`ml-2 font-extrabold ${provk || r?.reason === "zone_confirm" ? "text-amber-700" : ok ? "text-emerald-700" : "text-rose-700"}`}
                        >
                          {provk ? "À confirmer" : ok ? "Disponible" : r?.reason === "zone_confirm" ? "À confirmer" : "Indisponible"}
                        </span>
                      </p>
                      {k === "photobooth" && !ok && r?.message && <p className="text-xs text-slate-600 mt-0.5">{r.message}</p>}
                      {k === "photobooth" && r?.reason === "zone_confirm" && (
                        <ContactButtons phone={settings?.contact_phone} email={settings?.notification_email} prefix={multi ? `public-line-${k}-contact` : "public-line-photobooth-contact"} />
                      )}
                      {k !== "photobooth" && !provk && eqSubline(k, r) && (
                        <p className="text-xs text-slate-600 mt-0.5" data-testid={!multi ? "public-equipment-price" : undefined}>{eqSubline(k, r)}</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            {multi && (
              <p data-testid="public-global-verdict" className={`font-heading text-lg font-extrabold tracking-tight flex items-center gap-2 ${((result.provisional_kinds || []).length ? GLOBALS.provisional : g).text}`}>
                {(() => { const ge = (result.provisional_kinds || []).length ? GLOBALS.provisional : g; return <><ge.Icon className="h-5 w-5 shrink-0" /> {ge.label}</>; })()}
              </p>
            )}
            {result.global === "none" && (
              <p className="text-xs text-slate-500">Contactez-nous directement : une solution peut parfois être trouvée.</p>
            )}
          </section>
        )}

        {pbR?.available && (
          <section className="rounded-2xl border border-emerald-300 bg-emerald-50 p-5 space-y-4">
            <div className="rounded-xl bg-white border border-emerald-200 p-4 space-y-1.5" data-testid="public-price-detail">
              <div className="flex justify-between text-sm"><span className="text-slate-600">Formule {pbR.formula.label}</span><span className="font-mono tabular-nums">{fmtEUR(pbR.formula.price)}</span></div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Livraison et reprise ({pbR.dist_km} km de Lesménils)</span>
                <span className="font-semibold text-emerald-700">INCLUSES</span>
              </div>
              <div className="flex justify-between text-sm" data-testid="public-supplement-line">
                <span className="text-slate-600">Supplément kilométrique estimé</span>
                <span className="font-mono tabular-nums">{pbR.supplement > 0 ? `+ ${fmtEUR(pbR.supplement)}` : fmtEUR(0)}</span>
              </div>
              {cabineOk && (
                <div className="flex justify-between text-sm" data-testid="public-cabine-line">
                  <span className="text-slate-600">Cabine Audio Guest Book (à retirer à Lesménils)</span>
                  <span className="font-mono tabular-nums">+ {fmtEUR(cabinePrice)}</span>
                </div>
              )}
              {agbOffert && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Audio Guest Book</span>
                  <span className="font-semibold text-emerald-700">OFFERT</span>
                </div>
              )}
              <div className="flex justify-between font-heading font-extrabold text-lg pt-2 border-t border-slate-200">
                <span>Total estimé</span><span data-testid="public-total" className="font-mono tabular-nums">{fmtEUR(total)}</span>
              </div>
              <p className="text-xs text-slate-500 pt-1" data-testid="public-estimation-note">
                Estimation calculée selon le lieu renseigné. Le montant définitif du déplacement sera confirmé après vérification de l'adresse exacte de votre événement et peut légèrement varier.
              </p>
            </div>

            {agbOffert && (
              <div data-testid="public-agb-notice" className="rounded-xl border-2 border-amber-400 bg-amber-50 p-4 shadow-sm">
                <p className="flex flex-wrap items-center gap-2 font-extrabold text-slate-900 text-base">
                  <Gift className="h-5 w-5 text-amber-600" />
                  Audio Guest Book OFFERT
                  <span className="rounded-full bg-amber-400 px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-slate-900">Disponible pour votre date</span>
                </p>
                <p className="text-xs text-slate-600 mt-1">
                  Offert avec votre location de photobooth — remis lors de la livraison, rien à retirer.
                  <strong> Il sera inclus dans votre demande.</strong>
                </p>
              </div>
            )}
          </section>
        )}

        {result && result.consultation_id && result.global !== "none" && !contactSent && (
          <section data-testid="public-contact-block" className="rounded-2xl border border-slate-200 bg-white p-4 space-y-2">
            <p className="text-sm font-bold text-slate-800">Être recontacté·e ? (optionnel)</p>
            <p className="text-xs text-slate-500">Laissez vos coordonnées : Photobooth54 confirme personnellement chaque réservation.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Input data-testid="public-contact-name" placeholder="Prénom / Nom" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
              <Input data-testid="public-contact-email" type="email" placeholder="Email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
              <Input data-testid="public-contact-phone" placeholder="Téléphone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
            </div>
            <Button data-testid="public-contact-submit" variant="outline" onClick={sendContact} disabled={sendingContact} className="w-full font-semibold">
              {sendingContact && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Envoyer mes coordonnées
            </Button>
          </section>
        )}
        {contactSent && <p data-testid="public-contact-success" className="text-sm font-semibold text-emerald-700">✓ Coordonnées transmises — merci !</p>}

        {result && !result.weekday && !result.provisional && !result.last_minute && result.global !== "zone_confirm" && result.global !== "none" && (
          <section className="space-y-3">
            {pbR?.available && BOOKING_LINKS[formula] && (
              <a
                href={BOOKING_LINKS[formula]}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="public-booking-link"
                className="flex items-center justify-center gap-2 w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-base h-12 transition-colors"
              >
                RÉSERVER CETTE FORMULE <ExternalLink className="h-4 w-4" />
              </a>
            )}
            {cabineOk && BOOKING_LINKS.cabine && (
              <a
                href={BOOKING_LINKS.cabine}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="public-booking-link-cabine"
                className="flex items-center justify-center gap-2 w-full rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-11 transition-colors"
              >
                Réserver la Cabine en ligne <ExternalLink className="h-4 w-4" />
              </a>
            )}
            {agbOk && !agbOffert && BOOKING_LINKS.agb && (
              <a
                href={BOOKING_LINKS.agb}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="public-booking-link-agb"
                className="flex items-center justify-center gap-2 w-full rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-11 transition-colors"
              >
                Réserver l'Audio Guest Book en ligne <ExternalLink className="h-4 w-4" />
              </a>
            )}
            {!showForm && !sent && (
              <Button data-testid="public-request-button" variant="outline" onClick={() => setShowForm(true)} className="w-full font-semibold h-11 transition-colors">
                Ou faire une demande de réservation
              </Button>
            )}
            <p className="text-xs text-slate-500 text-center" data-testid="public-booking-note">
              Le bouton ouvre la page Photobooth54 correspondante pour régler l'acompte de la formule. Le solde et l'éventuel supplément déplacement sont confirmés ensemble après votre réservation — votre date n'est bloquée qu'après validation par Photobooth54.
            </p>
          </section>
        )}

        {showForm && !sent && (
          <section data-testid="public-request-form" className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
            <h2 className="font-heading text-lg font-bold">Votre demande</h2>
            <Input data-testid="public-request-name" placeholder="Nom / prénom *" value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} />
            <Input data-testid="public-request-phone" placeholder="Téléphone" value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} />
            <Input data-testid="public-request-email" type="email" placeholder="Email" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} />
            <textarea data-testid="public-request-message" rows={3} placeholder="Message (adresse, horaires, questions…)" value={message} onChange={(e) => setMessage(e.target.value)} className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm" />
            <Button data-testid="public-request-submit" onClick={sendRequest} disabled={sending} className="w-full bg-sky-500 hover:bg-sky-600 text-white font-semibold transition-colors">
              {sending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Envoyer ma demande
            </Button>
            <p className="text-xs text-slate-400">Votre demande nous est transmise : nous confirmons la réservation sous 24 h.</p>
          </section>
        )}

        {sent && (
          <section data-testid="public-success" className="rounded-2xl border border-emerald-300 bg-emerald-50 p-5">
            <p className="font-heading text-lg font-extrabold text-emerald-700">Demande envoyée !</p>
            <p className="text-sm text-slate-700 mt-1">Merci {client.name} — nous revenons vers vous très vite pour confirmer votre réservation du {date}.</p>
          </section>
        )}
      </main>
    </div>
  );
}
